import React from 'react';
import Input from '../Input-new/Input';
import Dropdown from '../Dropdown/Dropdown';
import Alert from '../Alert';
import {
    updateTeamMember,
    resetTeamMemberPassword,
    activateTeamMember,
    deactivateTeamMember,
    deleteTeamMember,
    getTeamMemberCrmCredentials,
    saveTeamMemberCrmCredentials,
} from '../../requests/userManagement';
import roles from '../../roles';
import { accessLevelsApi } from '../../requests/accessLevels';
import { getCurrentAccess } from '../../access';
import { isFounderOnlyAccount } from '../../access-levels';
import { convertDateFormatYYYYMMDD } from '../../utils';
import './TeamMemberAccountTab.scss';

const STATUS_OPTIONS = [
    { code: 'active', name: 'Active' },
    { code: 'inactive', name: 'Inactive' },
    { code: 'registered', name: 'Registered' },
];

/** UTC, because the server compares against now() and the app runs on UTC. */
function todayYYYYMMDD() {
    return new Date().toISOString().slice(0, 10);
}

/**
 * Mirrors TeamMember::isTerminated() — a date that has arrived ends access on
 * its own, whether or not STATUS_CODE was ever flipped to match.
 */
function terminationHasArrived(terminationDate) {
    if (!terminationDate) return false;

    const datePart = String(terminationDate).trim().split(/[ T]/)[0];
    const ymd = convertDateFormatYYYYMMDD(datePart);

    // Anything the app itself wrote parses; a value that does not is not a date.
    return /^\d{4}-\d{2}-\d{2}$/.test(ymd) && ymd <= todayYYYYMMDD();
}

/**
 * Account Settings Tab Component
 * 
 * Handles authentication credentials and account status for Team Members.
 * 
 * MODES:
 * 1. NEW USER MODE (no ARCIMEDES_ROW_ID):
 *    - Shows username, password, status, permissions fields
 *    - All fields are synced with parent state via props
 *    - No local save button (saves via parent's main Save button)
 * 
 * 2. EXISTING USER MODE (has ARCIMEDES_ROW_ID):
 *    - Shows current account status with badge
 *    - Provides isolated action buttons (Activate/Deactivate/Reset Password)
 *    - Local "Update Account Info" button for username/permissions
 *    - Password field is hidden (use "Reset Password" instead)
 */
export default class TeamMemberAccountTab extends React.Component {
    constructor(props) {
        super(props);
        
        this.state = {
            // Local state for existing user updates
            editUsername: '',
            accessLevelOptions: null,

            // Password reset
            newPassword: '',
            showPasswordResetForm: false,
            
            // Delete confirmation
            showDeleteConfirm: false,

            // UI state
            isBusy: false,
            error: null,
            success: null,

            // CRM Hot List credentials
            crmToken: '',
            crmConfigured: false,
            crmUserId: '',
            crmOpportunityId: '',
            crmTokenHint: null,
            crmBusy: false,
            crmError: null,
            crmSuccess: null,
        };

        this.handleUpdateAccount = this.handleUpdateAccount.bind(this);
        this.handleResetPassword = this.handleResetPassword.bind(this);
        this.handleActivate = this.handleActivate.bind(this);
        this.handleDeactivate = this.handleDeactivate.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleSaveCrmCredentials = this.handleSaveCrmCredentials.bind(this);
    }

    componentDidMount() {
        this.initializeEditFields();
        this.loadCrmCredentials();
        accessLevelsApi.options()
            .then((options) => this.setState({ accessLevelOptions: options }))
            .catch(() => this.setState({ accessLevelOptions: [] }));
    }

    levelName(code) {
        const options = this.state.accessLevelOptions || [];
        const wanted = code ? String(code).trim().toLowerCase() : '';
        const match = options.find((o) => o.code === wanted);
        return match ? match.name : (wanted || null);
    }

    componentDidUpdate(prevProps) {
        const currentId = this.props.data ? this.props.data.ARCIMEDES_ROW_ID : undefined;
        const prevId = prevProps.data ? prevProps.data.ARCIMEDES_ROW_ID : undefined;
        if (currentId !== prevId) {
            this.initializeEditFields();
            this.loadCrmCredentials();
        }
    }

    initializeEditFields() {
        const { data } = this.props;
        if (data && data.ARCIMEDES_ROW_ID) {
            this.setState({
                editUsername: data.USERNAME || '',
                editAccessLevel: (data.ACCESS_LEVEL ? String(data.ACCESS_LEVEL).trim().toLowerCase() : '') || '',
            });
        }
    }

    /**
     * Load the CRM Hot List setup status for this team member.
     *
     * The token itself never comes back from the server, so the input always
     * starts empty; `crmTokenHint` is what tells the admin one is already stored.
     */
    async loadCrmCredentials() {
        const { data } = this.props;
        const email = data && data.EMAIL;

        // The credentials endpoint is for those who may change account settings.
        if (!data || !data.ARCIMEDES_ROW_ID || !email || !this.canEdit()) return;

        this.setState({ crmBusy: true, crmError: null });

        try {
            const res = await getTeamMemberCrmCredentials(email);
            this.setState({
                crmBusy: false,
                crmToken: '',
                crmConfigured: !!res.configured,
                crmUserId: res.crmUserId || '',
                crmOpportunityId: res.crmOpportunityId || '',
                crmTokenHint: res.tokenHint || null,
            });
        } catch (err) {
            this.setState({
                crmBusy: false,
                crmError: err.message || 'Failed to load CRM credentials',
            });
        }
    }

    /**
     * Verify the pasted token with the CRM and store the resolved credentials.
     */
    async handleSaveCrmCredentials() {
        const { data } = this.props;
        const { crmToken } = this.state;
        const email = data && data.EMAIL;

        if (!email) {
            this.setState({ crmError: 'This team member has no email address.' });
            return;
        }

        if (!crmToken.trim()) {
            this.setState({ crmError: 'Please enter a CRM API token.' });
            return;
        }

        this.setState({ crmBusy: true, crmError: null, crmSuccess: null });

        try {
            const res = await saveTeamMemberCrmCredentials(email, crmToken.trim());
            this.setState({
                crmBusy: false,
                crmToken: '',
                crmConfigured: true,
                crmSuccess: 'CRM credentials verified and saved.',
                crmUserId: res.crmUserId ? String(res.crmUserId) : '',
                crmOpportunityId: res.crmOpportunityId ? String(res.crmOpportunityId) : '',
                crmTokenHint: res.tokenHint || null,
            });
        } catch (err) {
            this.setState({
                crmBusy: false,
                crmError: err.message || 'Failed to save CRM credentials',
            });
        }
    }

    /**
     * CRM Hot List credentials section (existing users only — the email has to
     * exist before the CRM can be asked about it).
     */
    /**
     * Whether the viewer may change this tab. The page decides it from their
     * access level (team.accounts); a caller that does not say gets a
     * read-only tab.
     */
    canEdit() {
        return !!this.props.canEdit;
    }

    renderCrmCredentials() {
        const {
            crmToken,
            crmConfigured,
            crmUserId,
            crmOpportunityId,
            crmTokenHint,
            crmBusy,
            crmError,
            crmSuccess,
        } = this.state;

        const locked = !this.canEdit();

        return (
            <div className="account-tab__section">
                <h3 className="account-tab__section-title">CRM Hot List Credentials</h3>
                <p className="account-tab__help-text">
                    To get an API token for this team member, raise a request with CRM support.
                    Paste it here and it is verified against the CRM, with their CRM user and
                    Hot List column resolved automatically. Without a token, Hot List actions
                    will not work for them.
                </p>

                {crmError && <Alert type="error">{crmError}</Alert>}
                {crmSuccess && <Alert type="success">{crmSuccess}</Alert>}

                <div className="account-tab__info-grid">
                    <div className="account-tab__info-row">
                        <span className="account-tab__label">Status:</span>
                        <span className={`account-tab__badge account-tab__badge--${crmConfigured ? 'active' : 'inactive'}`}>
                            {crmConfigured ? 'configured' : 'not configured'}
                        </span>
                    </div>

                    {crmConfigured && (
                        <React.Fragment>
                            <div className="account-tab__info-row">
                                <span className="account-tab__label">Stored token:</span>
                                <span className="account-tab__value">{crmTokenHint || '—'}</span>
                            </div>
                            <div className="account-tab__info-row">
                                <span className="account-tab__label">CRM User ID:</span>
                                <span className="account-tab__value">{crmUserId || '—'}</span>
                            </div>
                            <div className="account-tab__info-row">
                                <span className="account-tab__label">Hot List Column ID:</span>
                                <span className="account-tab__value">{crmOpportunityId || '—'}</span>
                            </div>
                        </React.Fragment>
                    )}
                </div>

                <Input
                    className="account-tab__input"
                    label={crmConfigured ? 'Replace CRM API Token' : 'CRM API Token'}
                    name="crmToken"
                    type="password"
                    value={crmToken}
                    handleParentChange={(name, value) =>
                        this.setState({ crmToken: value, crmError: null, crmSuccess: null })
                    }
                    viewMode={false}
                    placeholder="e.g., 123456|xxxxxxxx"
                    autoComplete="off"
                    disabled={crmBusy || locked}
                />

                <div className="account-tab__actions">
                    <button
                        type="button"
                        className="button-new positive"
                        onClick={this.handleSaveCrmCredentials}
                        disabled={crmBusy || locked || !crmToken.trim()}
                    >
                        {crmBusy ? 'Verifying with CRM...' : 'Verify & Save Token'}
                    </button>
                </div>
            </div>
        );
    }
    
    /**
     * Update the username (and access level) for an existing user.
     *
     * IS_AGENT is deliberately not sent from here: it is the Role field —
     * Summit/Peak x Employee/Agent — already edited on the Team Member tab, and
     * this tab used to offer its own dropdown for it with values the rest of the
     * app does not recognise, dropping people off the public Peak team page.
     * The endpoint itself is admin-only.
     */
    async handleUpdateAccount() {
        const { data, onRefresh, canAssignAccessLevel } = this.props;
        const { editUsername, editAccessLevel } = this.state;

        if (!data || !data.ARCIMEDES_ROW_ID) return;

        this.setState({ isBusy: true, error: null, success: null });

        const payload = {
            username: editUsername,
        };
        // Only a Founder may set it; the server refuses it from anyone else.
        if (canAssignAccessLevel && editAccessLevel !== ((data.ACCESS_LEVEL ? String(data.ACCESS_LEVEL).trim().toLowerCase() : '') || '')) {
            payload.access_level = editAccessLevel || null;
        }

        try {
            await updateTeamMember(data.ARCIMEDES_ROW_ID, payload);
            
            this.setState({
                isBusy: false,
                success: 'Account info updated successfully',
            });
            
            // Refresh parent data
            if (onRefresh) {
                await onRefresh();
            }
        } catch (err) {
            this.setState({
                isBusy: false,
                error: err.message || 'Failed to update account info',
            });
        }
    }
    
    /**
     * Reset password for existing user.
     */
    async handleResetPassword(e) {
        e.preventDefault();
        const { data } = this.props;
        const { newPassword } = this.state;
        
        if (!data || !data.ARCIMEDES_ROW_ID) return;
        if (!newPassword) {
            this.setState({ error: 'New password is required' });
            return;
        }
        
        this.setState({ isBusy: true, error: null, success: null });
        
        try {
            await resetTeamMemberPassword(data.ARCIMEDES_ROW_ID, newPassword);
            
            this.setState({
                isBusy: false,
                success: 'Password reset successfully',
                newPassword: '',
                showPasswordResetForm: false,
            });
        } catch (err) {
            this.setState({
                isBusy: false,
                error: err.message || 'Failed to reset password',
            });
        }
    }
    
    /**
     * Activate user account.
     */
    async handleActivate() {
        const { data, onRefresh } = this.props;
        
        if (!data || !data.ARCIMEDES_ROW_ID) return;
        
        this.setState({ isBusy: true, error: null, success: null });
        
        try {
            await activateTeamMember(data.ARCIMEDES_ROW_ID);
            
            this.setState({
                isBusy: false,
                success: 'Account activated successfully',
            });
            
            if (onRefresh) {
                await onRefresh();
            }
        } catch (err) {
            this.setState({
                isBusy: false,
                error: err.message || 'Failed to activate account',
            });
        }
    }
    
    /**
     * Deactivate user account.
     */
    async handleDeactivate() {
        const { data, onRefresh } = this.props;
        
        if (!data || !data.ARCIMEDES_ROW_ID) return;
        
        this.setState({ isBusy: true, error: null, success: null });
        
        try {
            await deactivateTeamMember(data.ARCIMEDES_ROW_ID);
            
            this.setState({
                isBusy: false,
                success: 'Account deactivated successfully',
            });
            
            if (onRefresh) {
                await onRefresh();
            }
        } catch (err) {
            this.setState({
                isBusy: false,
                error: err.message || 'Failed to deactivate account',
            });
        }
    }
    
    /**
     * Permanently delete user account.
     */
    async handleDelete() {
        const { data, onRefresh } = this.props;
        
        if (!data || !data.ARCIMEDES_ROW_ID) return;
        
        this.setState({ isBusy: true, error: null, success: null });
        
        try {
            await deleteTeamMember(data.ARCIMEDES_ROW_ID);
            
            this.setState({
                isBusy: false,
                success: 'Account permanently deleted',
                showDeleteConfirm: false,
            });
            
            // Redirect back to list after short delay
            setTimeout(() => {
                window.location.href = `${arc.path.base}teamMembers`;
            }, 1500);
        } catch (err) {
            this.setState({
                isBusy: false,
                error: err.message || 'Failed to delete account',
                showDeleteConfirm: false,
            });
        }
    }
    
    /**
     * Render mode for NEW users (no ARCIMEDES_ROW_ID).
     * Fields are synced with parent state via props.
     */
    renderNewUserMode() {
        const { data, handleChangeInput, handleChangeDropdown, viewMode } = this.props;
        
        return (
            <div className="account-tab__section">
                <h3 className="account-tab__section-title">Login Credentials</h3>
                <p className="account-tab__help-text">
                    These credentials will be created when you save this team member.
                </p>
                
                <Input
                    className="account-tab__input"
                    label="Username *"
                    name="USERNAME"
                    value={data.USERNAME || ''}
                    handleParentChange={handleChangeInput}
                    viewMode={viewMode}
                    placeholder="e.g., johndoe"
                    helpText="Used for logging into the system"
                />
                
                <Input
                    className="account-tab__input"
                    label="Initial Password *"
                    name="PASSWORD"
                    type="password"
                    value={data.PASSWORD || ''}
                    handleParentChange={handleChangeInput}
                    viewMode={viewMode}
                    placeholder="Minimum 8 characters"
                    helpText="User can change this after first login"
                    autoComplete="new-password"
                />
                
                <Dropdown
                    className="account-tab__input"
                    label="Account Status"
                    name="STATUS_CODE"
                    options={STATUS_OPTIONS}
                    optionKey="code"
                    optionLabelKey="name"
                    value={data.STATUS_CODE || 'active'}
                    valueKey="STATUS_CODE"
                    data={{ STATUS_CODE: data.STATUS_CODE || 'active' }}
                    handleParentChange={handleChangeDropdown}
                    viewMode={viewMode}
                />
                
                <Alert type="info">
                    Click the main "Save" button to create this team member with their login
                    credentials. Set their Role on the Team Member tab.
                </Alert>
            </div>
        );
    }
    
    /**
     * Render mode for EXISTING users (has ARCIMEDES_ROW_ID).
     * Shows current status and provides isolated action buttons.
     */
    renderExistingUserMode() {
        const { data, canAssignAccessLevel } = this.props;
        const {
            editUsername,
            editAccessLevel = '',
            newPassword,
            showPasswordResetForm,
            showDeleteConfirm,
            isBusy,
        } = this.state;

        // This tab is self-contained: every control here calls the account API
        // directly and saves itself, so it must NOT be gated on the parent's
        // `viewMode`. It used to be, and because the parent hides its Edit button
        // while this tab is open, viewMode could never be cleared from here —
        // leaving every button on the tab permanently disabled. Access is decided
        // by the viewer's access level instead.
        // A Founder's or an Admin's account is the Founder's to change (their own excepted); the server refuses anyone else.
        const viewer = getCurrentAccess();
        const founderOnlyAccount = isFounderOnlyAccount(viewer, this.state.accessLevelOptions, data.ACCESS_LEVEL, data.ARCIMEDES_ROW_ID);
        const locked = !this.canEdit() || founderOnlyAccount;

        // A termination date that has arrived blocks the login whatever
        // STATUS_CODE says, so it decides what this tab reports.
        const isTerminated = terminationHasArrived(data.TERMINATION_DATE);
        const isActive = !isTerminated && data.STATUS_CODE === 'active';
        // `USERNAME` can be null; treat that as "" so an untouched field is not
        // read as an edit.
        const currentAccessLevel = (data.ACCESS_LEVEL ? String(data.ACCESS_LEVEL).trim().toLowerCase() : '') || '';
        // Only the Founder gives or takes the Founder and Admin levels (the server refuses anyone else).
        const isFounder = getCurrentAccess().isFounder;
        const currentLevel = (this.state.accessLevelOptions || []).find((o) => o.code === currentAccessLevel);
        const levelLocked = !isFounder && !!currentLevel && !!currentLevel.founder_only;
        const hasChanges = editUsername !== (data.USERNAME || '')
            || (canAssignAccessLevel && editAccessLevel !== currentAccessLevel);

        // IS_AGENT is the Role field (Summit/Peak x Employee/Agent). Read the
        // label from the same list the Team Member tab edits, so the two agree
        // and legacy codes outside that list stay visible instead of being
        // silently relabelled.
        const role = roles.find((r) => r.code === data.IS_AGENT);
        const roleName = role
            ? role.name
            : (data.IS_AGENT ? `Legacy (${data.IS_AGENT})` : 'Not set');
        
        return (
            <div className="account-tab">
                {locked && (
                    <Alert type="info">
                        {founderOnlyAccount
                            ? "Only the Founder can change a Founder's or an Admin's account."
                            : 'Account settings are read-only for your access level. Ask an admin to make changes here.'}
                    </Alert>
                )}

                {/* Account Status Section */}
                <div className="account-tab__section">
                    <h3 className="account-tab__section-title">Account Status</h3>

                    <div className="account-tab__info-grid">
                        <div className="account-tab__info-row">
                            <span className="account-tab__label">Username:</span>
                            <span className="account-tab__value">{data.USERNAME || 'Not set'}</span>
                        </div>
                        
                        <div className="account-tab__info-row">
                            <span className="account-tab__label">Email:</span>
                            <span className="account-tab__value">{data.EMAIL}</span>
                        </div>
                        
                        <div className="account-tab__info-row">
                            <span className="account-tab__label">Status:</span>
                            <span className={`account-tab__badge account-tab__badge--${isTerminated ? 'inactive' : (data.STATUS_CODE || 'inactive')}`}>
                                {isTerminated ? 'terminated' : (data.STATUS_CODE || 'inactive')}
                            </span>
                        </div>
                        
                        <div className="account-tab__info-row">
                            <span className="account-tab__label">Role:</span>
                            <span className="account-tab__value">{roleName}</span>
                        </div>

                        <div className="account-tab__info-row">
                            <span className="account-tab__label">Access Level:</span>
                            <span className="account-tab__value">{this.levelName(data.ACCESS_LEVEL) || 'None'}</span>
                        </div>
                    </div>
                    
                    <div className="account-tab__actions">
                        {isActive ? (
                            <button
                                type="button"
                                className="button-new negative"
                                onClick={this.handleDeactivate}
                                disabled={isBusy || locked}
                            >
                                {isBusy ? 'Processing...' : 'Deactivate Account'}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="button-new positive"
                                onClick={this.handleActivate}
                                disabled={isBusy || locked || isTerminated}
                            >
                                {isBusy ? 'Processing...' : 'Activate Account'}
                            </button>
                        )}
                    </div>

                    {isTerminated && (
                        <p className="account-tab__help-text">
                            Clear the Termination Date on the Team Member tab before activating this account.
                        </p>
                    )}
                </div>
                
                {/* Update Account Info Section */}
                <div className="account-tab__section">
                    <h3 className="account-tab__section-title">Update Account Info</h3>
                    <p className="account-tab__help-text">
                        Role is set on the Team Member tab.
                    </p>

                    <Input
                        className="account-tab__input"
                        label="Username"
                        name="editUsername"
                        value={editUsername}
                        handleParentChange={(name, value) => this.setState({ editUsername: value })}
                        viewMode={locked}
                    />

                    {/* Only a member who manages access levels assigns them (access.manage). */}
                    {canAssignAccessLevel && (
                        <React.Fragment>
                        <Dropdown
                            className="account-tab__input account-tab__access-level"
                            label="Access Level"
                            name="ACCESS_LEVEL"
                            options={this.state.accessLevelOptions || []}
                            optionKey="code"
                            optionLabelKey="name"
                            value={editAccessLevel}
                            valueKey="ACCESS_LEVEL"
                            data={{ ACCESS_LEVEL: editAccessLevel }}
                            viewMode={levelLocked}
                            isOptionDisabled={(option) => !isFounder && !!option.founder_only}
                            // A member's level is never cleared: ignore an emptied selection.
                            handleParentChange={(changed) => changed.ACCESS_LEVEL && this.setState({ editAccessLevel: changed.ACCESS_LEVEL })}
                        />
                        {levelLocked && <p className="account-tab__help-text">Only the Founder can change this member's level.</p>}
                        {!isFounder && !levelLocked && <p className="account-tab__help-text">Only the Founder can give the Founder or Admin level.</p>}
                        </React.Fragment>
                    )}

                    {hasChanges && (
                        <div className="account-tab__actions">
                            <button
                                type="button"
                                className="button-new positive"
                                onClick={this.handleUpdateAccount}
                                disabled={isBusy || locked}
                            >
                                {isBusy ? 'Saving...' : 'Update Account Info'}
                            </button>
                        </div>
                    )}
                </div>
                
                {/* Password Reset Section */}
                <div className="account-tab__section">
                    <h3 className="account-tab__section-title">Password Management</h3>
                    
                    {!showPasswordResetForm ? (
                        <button
                            type="button"
                            className="button-new negative"
                            onClick={() => this.setState({ showPasswordResetForm: true, error: null, success: null })}
                            disabled={isBusy || locked}
                        >
                            Reset Password
                        </button>
                    ) : (
                        <form onSubmit={this.handleResetPassword} className="account-tab__form">
                            <Input
                                className="account-tab__input"
                                label="New Password"
                                name="newPassword"
                                type="password"
                                value={newPassword}
                                handleParentChange={(name, value) => this.setState({ newPassword: value })}
                                viewMode={false}
                                placeholder="Minimum 8 characters"
                                autoComplete="new-password"
                            />
                            
                            <div className="account-tab__actions">
                                <button
                                    type="submit"
                                    className="button-new positive"
                                    disabled={isBusy || !newPassword}
                                >
                                    {isBusy ? 'Saving...' : 'Set New Password'}
                                </button>
                                <button
                                    type="button"
                                    className="button-new negative"
                                    onClick={() => this.setState({ showPasswordResetForm: false, newPassword: '' })}
                                    disabled={isBusy}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </div>
                
                {/* CRM Hot List Credentials */}
                {this.renderCrmCredentials()}

                {/* Danger Zone */}
                <div className="account-tab__section account-tab__section--danger">
                    <h3 className="account-tab__section-title">Danger Zone</h3>
                    <p className="account-tab__help-text">
                        Permanently delete this account. This action cannot be undone.
                    </p>
                    
                    {!showDeleteConfirm ? (
                        <button
                            type="button"
                            className="button-new account-tab__btn--danger"
                            onClick={() => this.setState({ showDeleteConfirm: true, error: null, success: null })}
                            disabled={isBusy || locked}
                        >
                            Delete Account Permanently
                        </button>
                    ) : (
                        <div className="account-tab__confirm">
                            <p className="account-tab__confirm-text">
                                Are you sure? This will permanently delete <strong>{data.FIRST_NAME} {data.LAST_NAME}</strong>'s account and cannot be undone.
                            </p>
                            <div className="account-tab__actions">
                                <button
                                    type="button"
                                    className="button-new account-tab__btn--danger"
                                    onClick={this.handleDelete}
                                    disabled={isBusy}
                                >
                                    {isBusy ? 'Deleting...' : 'Yes, Delete Permanently'}
                                </button>
                                <button
                                    type="button"
                                    className="button-new negative"
                                    onClick={() => this.setState({ showDeleteConfirm: false })}
                                    disabled={isBusy}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
    
    render() {
        const { data } = this.props;
        const { error, success } = this.state;
        
        const isNewUser = !data || !data.ARCIMEDES_ROW_ID;
        
        return (
            <div className="account-tab">
                {error && <Alert type="error">{error}</Alert>}
                {success && <Alert type="success">{success}</Alert>}
                
                {isNewUser ? this.renderNewUserMode() : this.renderExistingUserMode()}
            </div>
        );
    }
}
